<script lang="ts">
    import {getLocalization} from 'src/core';
    import type {Facet} from 'src/typings';
    import type {Conjunction, Criterion} from '../search-criteria/criteria.types';
    import {findFirst} from 'shared/utils/array-utils';
    import {exists, identifiedValuesEquals, isDefined} from 'shared/utils/custom-utils';
    import {createEventDispatcher} from 'svelte';
    import CriterionsFormManager from '../search-criteria/criterions-form-manager';
    import CriterionMarshaller from '../search-criteria/criterion-marshallers/criterion-marshaller';
    import CriterionFactory from '../search-criteria/criterion-factories/criterion.factory';
    import {isEqual} from 'lodash';
    import KpSearchFacet from './KpSearchFacet.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';

    export let availableFacets: Facet[];
    export let selectedFacetConjunction: Conjunction;
    export let defaultExpandedFacets: string[];
    export let isAnySelected = false;

    const criterionsFormManager = new CriterionsFormManager(new CriterionFactory());
    const criterionMarshaller = new CriterionMarshaller(new CriterionFactory());

    const localize = getLocalization();
    const dispatch = createEventDispatcher<{ 'restrictions-change': Conjunction }>();

    let criterions: Criterion[] = [];

    $: refresh(availableFacets, selectedFacetConjunction);

    function changeRestrictions() {
        dispatch('restrictions-change', criterionMarshaller.fromCriterionsToConjunction(criterions));
    }

    export function deactivateAllKeys(): void {
        criterionsFormManager.clearCriterionValues();
        criterions = criterionsFormManager.getCriterions();
        isAnySelected = criterionsFormManager.isAnyCriterionFilled();
        changeRestrictions();
    }

    function isFacetInitiallyExpanded(criterion: Criterion): boolean {
        if (!criterion.isEmpty()) {
            return true;
        }
        return isDefined(defaultExpandedFacets.find((expandedFacetId) => expandedFacetId === criterion.field.id));
    }

    function refresh(updatedFacets: Facet[], updatedFacetRestriction: Conjunction): void {
        const filledCriterions = criterionMarshaller.fromConjunctionToCriterions(updatedFacetRestriction);

        criterionsFormManager.setCriterions(
            updatedFacets
                .map((facet) => {
                    // We try to find the criterion in the filled ones; if we don’t find it, we create a new one from the field
                    const filledCrit = findFirst(filledCriterions, (filledCriterion) => identifiedValuesEquals(filledCriterion.field, facet));
                    if (exists(filledCrit)) {
                        filledCrit.field = facet;
                        return filledCrit;
                    }

                    return criterionsFormManager.createCriterionByField(facet);
                })
        );

        if (!isEqual(criterions, criterionsFormManager.getCriterions())) {
            criterions = criterionsFormManager.getCriterions();
        }

        isAnySelected = criterionsFormManager.isAnyCriterionFilled();
    }
</script>

{#if availableFacets}
    <div class="panel-heading facets-panel-heading">
        <h2 class="unset-style">
            {localize(/* @kp-localization hledani.vybratZVyhledanych */ 'hledani.vybratZVyhledanych')}
        </h2>

        {#if isAnySelected}
            <KpButton buttonSize="xs" on:click={deactivateAllKeys}>
                {localize(/* @kp-localization hledani.zrusitVsechnaOznaceni */ 'hledani.zrusitVsechnaOznaceni')}
            </KpButton>
        {/if}
    </div>

    <div class="facets-panel-body">
        {#each criterions as criterion (criterion.field.id)}
            <KpSearchFacet {criterion}
                           initiallyExpanded={isFacetInitiallyExpanded(criterion)}
                           on:criterion-change={changeRestrictions}/>
        {/each}
    </div>

    {#if isAnySelected}
        <div class="panel-footer facets-panel-footer">
            <KpButton buttonSize="xs" on:click={deactivateAllKeys}>
                {localize(/* @kp-localization hledani.zrusitVsechnaOznaceni */ 'hledani.zrusitVsechnaOznaceni')}
            </KpButton>
        </div>
    {/if}
{/if}

<style lang="less">
    .facets-panel-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
    }

    .facets-panel-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
    }
</style>